/**
 * ¿Contesta el backend? — estado puro, sin dependencias.
 *
 * Vive aparte de `connectivity.ts` A PROPÓSITO: ese módulo arrastra el plugin
 * nativo de red (`@capacitor/network`), y el sondeo previo a guardar
 * (`checkConnectivity`) no tiene por qué cargar medio Capacitor para apuntar un
 * booleano. Mantenerlo separado evita además que cualquier test que toque el
 * sondeo tenga que mockear Capacitor entero.
 *
 * Por qué existe: «estar enganchado a una red» y «poder hablar con el backend»
 * no son lo mismo. En carretera se separan constantemente —el hotspot dice que
 * hay cobertura y no pasa un byte— y hasta 2026-09-11 la app solo miraba lo
 * primero: el sondeo se hacía por cada guardado y su veredicto se TIRABA, así
 * que las pantallas seguían esperando a la red y se quedaban girando.
 */

type Listener = () => void;

let reachable: boolean | null = null;
let listeners = new Set<Listener>();
// Sube con CADA prueba de que el servidor contesta (también cuando el veredicto
// ya era `true` y no cambia nada). El cortacircuitos lo compara antes y
// después de sondear: si mientras tanto alguien vio el servidor vivo, su «no»
// ya es viejo (spec 2026-10-09 § 9).
let positiveEvidence = 0;

/** `null` mientras no se sepa; `false` solo si un sondeo real falló. */
export function isBackendReachable(): boolean | null {
  return reachable;
}

/**
 * Apunta el resultado de un contacto real. Lo llaman el sondeo previo a guardar
 * y cualquier respuesta que sí vuelve (así se sale del modo sin red en cuanto
 * la cobertura regresa, sin esperar a otro sondeo).
 */
export function setBackendReachable(valor: boolean): void {
  if (valor) positiveEvidence += 1;
  if (reachable === valor) return;
  reachable = valor;
  listeners.forEach((listener) => listener());
}

export function subscribeBackendReachability(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Cuántas veces se ha visto el servidor contestar (solo crece). */
export function getPositiveEvidenceCount(): number {
  return positiveEvidence;
}

/** Solo para tests. */
export function _resetBackendReachabilityForTests(): void {
  reachable = null;
  listeners = new Set();
  positiveEvidence = 0;
}
